import React, { useCallback, useMemo } from 'react';

import {
  IconButton,
  Link,
  Card,
  CardHeader,
  CardContent,
  Help,
  DescriptionList,
  DescriptionItem,
  Stack,
  Typography,
  EditIcon,
  CloseIcon,
} from '@gogame/components';
import { NumberValueFormatter } from '@gogame/intl-ts';

import { StatusIndicator } from 'scenes/Campaigns/components/shared';

import {
  PERIODS,
  PERIOD,
  PERIOD_END_DATE,
  PERIOD_START_DATE,
  RATE,
  RATE_CURRENCY,
  RATE_VALUE,
  TYPE,
  TYPE_NAME,
  IS_READONLY,
  PENDING_CHANGES,
  ACTIVITY_TYPE,
  ACTIVITY_TYPE_UA,
  ACTIVITY_TYPE_RE,
  ACTIVITY_TYPE_TOTAL,
} from '../constants';

export const CostModelPreviewItem = ({
  model = {},
  index,
  onRemove,
  onEdit,
  removable,
  onOpenSpendAdjustmentDetails,
}) => {
  const {
    [PERIODS]: periods,
    [TYPE]: type,
    [IS_READONLY]: isReadonly,
    [PENDING_CHANGES]: pendingChanges,
  } = model;
  const lastPeriod = [...periods].pop();
  const { [PERIOD_START_DATE]: startDate, [PERIOD_END_DATE]: endDate } =
    lastPeriod[PERIOD] || {};
  const { [ACTIVITY_TYPE]: activityType } = lastPeriod;

  const { [RATE_VALUE]: value, [RATE_CURRENCY]: currency } =
    lastPeriod[RATE] || {};

  const range = [startDate, endDate];
  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);
  const handleEdit = useCallback(
    () => onEdit(index, model),
    [model, index, onEdit]
  );

  const title = useMemo(() => {
    switch (activityType) {
      case ACTIVITY_TYPE_UA:
        return `${type[TYPE_NAME]} (UA)`;
      case ACTIVITY_TYPE_RE:
        return `${type[TYPE_NAME]} (RE)`;
      case ACTIVITY_TYPE_TOTAL:
        return `${type[TYPE_NAME]} (Total)`;
      default:
        return type[TYPE_NAME];
    }
  }, [type[TYPE_NAME], activityType]);

  return (
    <Card elevation={6} data-cy-label={type[TYPE_NAME]}>
      <CardHeader
        title={title}
        action={
          <Stack direction={'row'} spacing={2}>
            <IconButton
              icon={<EditIcon />}
              tooltip={'Edit'}
              onClick={handleEdit}
            />
            {removable ? (
              <IconButton
                icon={<CloseIcon />}
                tooltip={'Remove'}
                onClick={handleRemove}
              />
            ) : null}
          </Stack>
        }
      />
      <CardContent>
        <DescriptionList stripEmpty={true} disableGutters={true} data={model}>
          <DescriptionItem
            label={'Event'}
            field={'subType.name'}
            type={'text'}
          />
          <DescriptionItem
            value={range}
            type={'dateRange'}
            label={'Period'}
            template={'LL'}
            timeZone={'utc'}
          />
          <DescriptionItem
            label={'Rate'}
            value={value}
            type={({ value }) => {
              return (
                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                  {!isReadonly ? <StatusIndicator status={'pending'} /> : null}
                  <Typography>
                    {pendingChanges?.spendAdjustmentId ? (
                      <Link
                        onClick={() =>
                          onOpenSpendAdjustmentDetails(
                            pendingChanges.spendAdjustmentId
                          )
                        }
                      >
                        <NumberValueFormatter.Currency
                          value={value}
                          currency={currency}
                          precision={2}
                        />
                      </Link>
                    ) : (
                      <NumberValueFormatter.Currency
                        value={value}
                        currency={currency}
                        precision={2}
                      />
                    )}
                  </Typography>
                  {pendingChanges?.message ? (
                    <Help>{pendingChanges.message}</Help>
                  ) : null}
                </Stack>
              );
            }}
          />
        </DescriptionList>
      </CardContent>
    </Card>
  );
};

export default CostModelPreviewItem;
